import { isPlatformServer } from '@angular/common';
import { Injectable, InjectionToken, Injector, PLATFORM_ID, Provider, REQUEST_CONTEXT, Type, inject } from '@angular/core';

import { PLATFORM } from '@frontend/vanilla/ssr';

import { ProductService } from '../products/product.service';

/**
 * @whatItDoes Application life cycle hook that is called when application is started.
 *
 * @description
 *
 * Services that implement this interface can be registered with `runOnAppInit(Service)` to run
 * when app is initializing (same as registering `APP_INITIALIZER`).
 *
 * @stable
 */
export interface OnAppInit {
    onAppInit(): void | Promise<any>;
}

/**
 * @stable
 */
export interface ProductBootstrapper {
    onLoad(): void | Promise<void>;

    onActivate(): void | Promise<void>;

    onDeactivate(): void | Promise<void>;
}

export const PRODUCT_BOOTSTRAPPER = new InjectionToken<ProductBootstrapper[]>('vn-product-bootstrapper');
export const ON_APP_INIT_BOOTSTRAPPER = new InjectionToken<OnAppInit[]>('vn-on-app-init-bootstrapper');

/**
 * @whatItDoes Registers a provider to invoke `onAppInit()` when the application is started.
 *
 * @stable
 */
export function runOnAppInit<T extends OnAppInit>(type: Type<T>): Provider[] {
    return [
        type,
        {
            provide: ON_APP_INIT_BOOTSTRAPPER,
            multi: true,
            useExisting: type,
        },
    ];
}

/**
 * @whatItDoes Registers a provider to invoke `onAppInit()` when the application is started on a browser and a noop on the server.
 *
 * @stable
 */
export function runOnBrowserAppInit<T extends OnAppInit>(type: Type<T>): Provider[] {
    return [
        type,
        {
            provide: ON_APP_INIT_BOOTSTRAPPER,
            multi: true,
            useFactory: () => (inject(PLATFORM).isBrowser ? inject(type) : onAppInitNoop),
        },
    ];
}
const onAppInitNoop = { onAppInit: () => void 0 };

/**
 * @whatItDoes Registers a provider to invoke when product changes.
 *
 * Invoke `onActivate()` when the product route is navigated to and `onDeactivate` when navigating away from a product route.
 * (e.g. Navigating from `/en/sports/path` to `/en/casino/path`).
 *
 * @stable
 */
export function provideProductBootstrapper<T extends ProductBootstrapper>(type: Type<T>): Provider[] {
    return [
        type,
        {
            provide: PRODUCT_BOOTSTRAPPER,
            multi: true,
            useExisting: type,
        },
    ];
}

/**
 * @whatItDoes Defines phases of {@link BootstrapperService}.
 *
 * @stable
 */
export enum BootstrapperPhase {
    None,
    AppInit,
    ProductLoad,
    ProductActivate,
    ProductDeactivate,
}

/**
 * @whatItDoes Runs application and product bootstrappers.
 *
 * @stable
 */
@Injectable({
    providedIn: 'root',
})
export class BootstrapperService {
    private _phase: BootstrapperPhase;

    /** Current phase that the bootstrapper is in. */
    get phase(): BootstrapperPhase {
        return this._phase;
    }

    private readonly injector = inject(Injector);
    private readonly productService = inject(ProductService);

    /* @internal */
    async runAppInit() {
        if (isPlatformServer(inject(PLATFORM_ID)) && inject(REQUEST_CONTEXT) === null) {
            return;
        }
        await this.runBootstrappers(BootstrapperPhase.AppInit, this.injector, ON_APP_INIT_BOOTSTRAPPER, (b) => b.onAppInit());
    }

    async runProductLoad(product: string) {
        await this.runBootstrappers(BootstrapperPhase.ProductLoad, this.getProductInjector(product), PRODUCT_BOOTSTRAPPER, (b) => b.onLoad());
    }

    async runProductActivation(product: string) {
        await this.runBootstrappers(BootstrapperPhase.ProductActivate, this.getProductInjector(product), PRODUCT_BOOTSTRAPPER, (b) => b.onActivate());
    }

    async runProductDeactivation(product: string) {
        await this.runBootstrappers(BootstrapperPhase.ProductDeactivate, this.getProductInjector(product), PRODUCT_BOOTSTRAPPER, (b) =>
            b.onDeactivate(),
        );
    }

    private async runBootstrappers<T>(phase: BootstrapperPhase, injector: Injector, token: InjectionToken<T[]>, fn: (b: T) => void | Promise<void>) {
        const bootstrappers = injector.get(token, []);

        this._phase = phase;

        await Promise.all(bootstrappers.map(fn));

        this._phase = BootstrapperPhase.None;
    }

    private getProductInjector(product: string): Injector {
        return this.productService.getMetadata(product).injector;
    }
}
